/**
 * What the room is doing that is not a seat taking a turn: fetching, and sorting.
 *
 * This lived in the Feed & filters rail, which is only mounted on the feed — so
 * the moment you opened Reports the room looked idle while eight lookups were in
 * flight. It belongs in the column that is always there, under "In the room" —
 * the seats, then the work that has no seat.
 *
 * In the room column the rows are STATUS ONLY (no `onPick`): clicking them there
 * changed a filter somewhere you could not see. The same rows sit under Lookups in
 * Feed & filters, and those are the ones that filter the feed.
 */
import { useState, type ReactNode } from 'react';
import { KIND_LABELS, LOOKUP_KINDS } from './kinds';
import { SORTING_BUDGETS, budgetLine, lookupLines, taggingLine, taggingNote, taggingTone, tokensShort } from './lookupActivity';
import type {
  AppealQueue,
  BoardroomAppealStatus,
  BoardroomItemKind,
  BoardroomLookupCounts,
  BoardroomLookupState,
  BoardroomTagging,
  SortingWaiting,
  SortingWaitingPick,
} from '../../types';

const HEADING = 'text-[11px] uppercase tracking-wide text-gray-400 mb-1';
const ROW =
  'w-full flex items-center justify-between px-2 py-0.5 rounded text-xs text-left transition';

const TONE: Record<string, string> = {
  busy: 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800',
  waiting: 'text-amber-700 dark:text-amber-300 hover:bg-gray-100 dark:hover:bg-gray-800',
  bad: 'text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800',
  quiet: 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800',
};
// The same tones with no hover, for the read-only rows.
const STATIC: Record<string, string> = {
  busy: 'text-gray-600 dark:text-gray-400',
  waiting: 'text-amber-700 dark:text-amber-300',
  bad: 'text-red-600 dark:text-red-400',
  quiet: 'text-gray-600 dark:text-gray-400',
};
const ACTIVE =
  'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 font-medium';

export function LookupActivity({
  counts,
  kindCounts,
  kind,
  state,
  onPick,
  heading = 'Looking up',
}: {
  counts: BoardroomLookupCounts;
  /** Per-kind totals; the room's own item counts when no lens is on. */
  kindCounts: Record<string, number>;
  kind: BoardroomItemKind | null;
  state: BoardroomLookupState | null;
  /** Absent = read-only status rows. */
  onPick?: (next: { kind?: BoardroomItemKind | null; lookup?: BoardroomLookupState | null }) => void;
  /** Null under the rail's Lookups row, which already names it. */
  heading?: string | null;
}) {
  const lines = lookupLines(counts, state);
  const kinds = LOOKUP_KINDS.filter(k => (kindCounts[k] ?? 0) > 0 || kind === k);
  if (!lines.length && !kinds.length) return null;
  return (
    <div data-testid="lookup-activity">
      {heading && <div className={HEADING}>{heading}</div>}
      {kinds.length > 0 && (
        <div className="mb-1" data-testid="lookup-kinds">
          {kinds.map(k => {
            const active = kind === k;
            const inner = (
              <>
                <span>{KIND_LABELS[k]}</span>
                <span className="tabular-nums">{kindCounts[k] ?? 0}</span>
              </>
            );
            return onPick ? (
              <button
                key={k}
                type="button"
                onClick={() => onPick({ kind: active ? null : k })}
                className={`${ROW} ${active ? ACTIVE : TONE.quiet}`}
                data-testid={`lookup-kind-${k}`}
              >
                {inner}
              </button>
            ) : (
              <div key={k} className={`${ROW} ${STATIC.quiet}`} data-testid={`lookup-kind-${k}`}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
      {lines.length > 0 && (
        <div data-testid="lookup-states">
          {lines.map(l => {
            const active = state === l.key;
            const inner = (
              <>
                <span>{l.label}</span>
                <span className="tabular-nums">{l.count}</span>
              </>
            );
            return onPick ? (
              <button
                key={l.key}
                type="button"
                onClick={() => onPick({ lookup: active ? null : l.key })}
                className={`${ROW} ${active ? ACTIVE : TONE[l.tone]}`}
                data-testid={`lookup-${l.key}`}
              >
                {inner}
              </button>
            ) : (
              <div key={l.key} className={`${ROW} ${STATIC[l.tone]}`} data-testid={`lookup-${l.key}`}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** WORK THAT HAS NO SEAT, as a section of the rail: a title the same size as the
 *  rows above it, a line between it and them, and on the right a dot and a word
 *  for what it is doing now. The rows underneath are the section's own. */
type SectionState = 'working' | 'waiting' | 'done' | 'stopped';
const DOT: Record<SectionState, string> = {
  working: 'bg-amber-500 animate-pulse',
  waiting: 'bg-amber-300',
  done: 'bg-emerald-500',
  stopped: 'bg-red-500',
};

function Section({
  title,
  state,
  word,
  hint,
  testId,
  children,
}: {
  title: string;
  state: SectionState;
  word: string;
  /** The longer story, on hover. */
  hint?: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div className="border-t border-gray-200 pt-3 dark:border-gray-700" data-testid={testId}>
      <div className="mb-1 flex items-center justify-between px-2" title={hint}>
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400" data-testid={`${testId}-state`}>
          <span className={`inline-block h-2 w-2 rounded-full ${DOT[state]}`} aria-hidden />
          {word}
        </span>
      </div>
      {children}
    </div>
  );
}

/** YOUR DAILY BUDGET FOR SORTING AFTER A DIG, where the sorting is shown: what
 *  it has spent, and a picker to change it. It is yours, for every room. */
function SortingBudget({
  tagging,
  onSet,
}: {
  tagging?: BoardroomTagging | null;
  onSet: (tokens: number) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const line = budgetLine(tagging);
  const cap = tagging?.after_budget?.cap;
  if (!line || cap == null) return null;
  const options = SORTING_BUDGETS.includes(cap) ? SORTING_BUDGETS : [...SORTING_BUDGETS, cap].sort((a, b) => a - b);
  return (
    <div className="mt-1 px-2 text-xs" data-testid="sorting-budget">
      {/* Spent is short and sits beside the picker; the whole sentence is on hover.
          Used up with items waiting is the one case worth a line of its own. */}
      {line.stopped && <div className="mb-1 text-amber-700 dark:text-amber-300">{line.text}</div>}
      <label className="flex items-center justify-between gap-2 text-gray-500 dark:text-gray-400" title={line.text}>
        <span>
          After digs{' '}
          <span className="tabular-nums">
            {tokensShort(tagging?.after_budget?.spent ?? 0)} of {tokensShort(cap)}
          </span>{' '}
          a day
        </span>
        <select
          value={cap}
          disabled={saving}
          onChange={async e => {
            setSaving(true);
            try {
              await onSet(Number(e.target.value));
            } finally {
              setSaving(false);
            }
          }}
          title="How much sorting may spend in a room each day after a dig ends. Sorting during a dig comes out of the dig's own budget."
          className="rounded border border-gray-300 bg-white px-1 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          data-testid="sorting-budget-select"
        >
          {options.map(n => (
            <option key={n} value={n}>
              {n === 0 ? 'off' : `${tokensShort(n)} tokens`}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

export function TaggingActivity({
  tagging,
  onSetBudget,
  waiting = null,
  onWaiting,
}: {
  tagging?: BoardroomTagging | null;
  /** Change your daily budget for sorting after digs. */
  onSetBudget?: (tokens: number) => Promise<void>;
  /** Which queue (and kind in it) the feed is narrowed to, if any. */
  waiting?: SortingWaitingPick | null;
  /** Narrow the feed to one queue or one kind in it, or (null) stop. */
  onWaiting?: (w: SortingWaitingPick | null) => void;
}) {
  // A story room sorts nothing: "no tag groups yet" there read as a fault.
  if (tagging?.not_used) return null;
  const line = taggingLine(tagging);
  const note = taggingNote(tagging);
  if (!line && !note) return null;
  const tone = taggingTone(tagging);
  // WHICH QUEUE IS BEING WORKED. The summary line is about the axes only, and
  // pulsed whenever the sorter was busy - so it blinked over numbers that
  // could not move while the sorter read items for content. The pulse goes on
  // the queue it is actually working: the axes first, as the sorter does.
  const working: SortingWaiting | null =
    tone !== 'working' ? null : (tagging?.waiting ?? 0) > 0 ? 'axes' : (tagging?.content_waiting ?? 0) > 0 ? 'content' : null;
  const word =
    tone === 'working' ? 'sorting now' : tone === 'parked' ? 'stopped' : tone === 'waiting' ? 'waiting' : 'up to date';
  const state: SectionState =
    tone === 'working' ? 'working' : tone === 'parked' ? 'stopped' : tone === 'waiting' ? 'waiting' : 'done';
  const axesHint = tagging?.axes?.length ? `Onto ${tagging.axes.join(', ')}` : '';
  return (
    <Section
      title="Sorting"
      state={state}
      word={word}
      hint={[line, axesHint].filter(Boolean).join(' · ') || undefined}
      testId="tagging"
    >
      {/* THE SUMMARY LINE only where the queues are not shown: with them, it said
          "90 waiting to be sorted" right above "not on the axes yet 90". */}
      {line && !onWaiting && (
        <div className="px-2 text-xs text-gray-600 dark:text-gray-400" data-testid="tagging-line" title={axesHint || undefined}>
          {line}
        </div>
      )}
      {/* A reason only when something is WRONG: "all sorted" is not a problem,
          and printing it as one is how a healthy room reads as broken. */}
      {note && (
        <div className="px-2 text-xs text-amber-700 dark:text-amber-300" data-testid="tagging-note">
          {note}
        </div>
      )}
      {/* WHAT IS STILL TO SORT, broken out like Lookups: each queue a row with
          its count, and under it the kinds of item waiting in it. Always
          there, even at 0. A click narrows the feed to those items - found but
          not sorted yet, so no lens can show them. */}
      {onWaiting && tagging && (
        <div className="mt-1" data-testid="sorting-queues">
          {(
            [
              ['axes', 'not tagged yet', tagging.waiting, tagging.waiting_kinds ?? {}],
              ['content', 'not tagged for what it holds yet', tagging.content_waiting ?? 0, tagging.content_waiting_kinds ?? {}],
            ] as [SortingWaiting, string, number, Record<string, number>][]
          ).map(([queue, label, n, kinds]) => {
            const picked = waiting?.queue === queue;
            const active = picked && !waiting?.kind;
            const pulse = working === queue ? 'text-amber-700 dark:text-amber-300 animate-pulse' : '';
            return (
              <div key={queue}>
                <button
                  type="button"
                  onClick={() => onWaiting(active ? null : { queue, kind: null })}
                  aria-pressed={active}
                  className={`${ROW} ${active ? ACTIVE : TONE.quiet}`}
                  title={working === queue ? 'The sorter is working on these now' : undefined}
                  data-testid={`sorting-waiting-${queue}`}
                >
                  <span className={pulse}>{label}</span>
                  <span className="tabular-nums">{n.toLocaleString()}</span>
                </button>
                {Object.entries(kinds)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, count]) => {
                    const kind = k as BoardroomItemKind;
                    const on = picked && waiting?.kind === kind;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => onWaiting(on ? null : { queue, kind })}
                        aria-pressed={on}
                        className={`${ROW} pl-5 ${on ? ACTIVE : TONE.quiet}`}
                        data-testid={`sorting-waiting-${queue}-${k}`}
                      >
                        <span>{KIND_LABELS[kind] ?? k}</span>
                        <span className="tabular-nums">{count.toLocaleString()}</span>
                      </button>
                    );
                  })}
              </div>
            );
          })}
        </div>
      )}
      {onSetBudget && <SortingBudget tagging={tagging} onSet={onSetBudget} />}
    </Section>
  );
}

/** APPEAL READS, the way Sorting shows the sorter: reading now, waiting for the next
 *  dig, read, and nothing to read. Status only. Null when the room does not have the
 *  Appeal table, because nothing reads there. */
export function AppealActivity({
  appeal,
  waiting = null,
  onWaiting,
}: {
  appeal?: BoardroomAppealStatus | null;
  /** What the feed is narrowed to, if anything. */
  waiting?: SortingWaitingPick | null;
  /** Narrow the feed to the companies behind a row, or (null) stop. Absent: status only. */
  onWaiting?: (w: SortingWaitingPick | null) => void;
}) {
  if (!appeal) return null;
  const state: SectionState = appeal.reading > 0 ? 'working' : appeal.waiting > 0 ? 'waiting' : 'done';
  const word = appeal.reading > 0 ? 'reading now' : appeal.waiting > 0 ? 'after the next dig' : 'up to date';
  const rows: [string, string, number, string][] = [
    ['reading', 'reading', appeal.reading, appeal.reading > 0 ? 'text-amber-700 dark:text-amber-300 animate-pulse' : ''],
    ['waiting', 'waiting to be read', appeal.waiting, appeal.waiting > 0 ? 'text-amber-700 dark:text-amber-300' : ''],
    ['read', 'read', appeal.read, ''],
    ['no_pages', 'no pages to read yet', appeal.no_pages, ''],
  ];
  const queueOf = (key: string) => `appeal_${key}` as AppealQueue;
  return (
    <Section
      title="Appeal"
      state={state}
      word={word}
      hint={`How each company pitches itself, read from its pages. ${appeal.read} of ${appeal.companies} companies read; the rest are read when a dig ends.`}
      testId="appeal-activity"
    >
      {rows.map(([key, label, n, tone]) => {
        const inner = (
          <>
            <span className={tone}>{label}</span>
            <span className="tabular-nums">{n.toLocaleString()}</span>
          </>
        );
        if (!onWaiting) {
          return (
            <div key={key} className={`${ROW} ${STATIC.quiet}`} data-testid={`appeal-${key}`}>
              {inner}
            </div>
          );
        }
        const active = waiting?.queue === queueOf(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => onWaiting(active ? null : { queue: queueOf(key), kind: null })}
            aria-pressed={active}
            className={`${ROW} ${active ? ACTIVE : TONE.quiet}`}
            title="Show these companies in the feed"
            data-testid={`appeal-${key}`}
          >
            {inner}
          </button>
        );
      })}
    </Section>
  );
}
